"use client"

import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import { marketingActionClass } from "@/components/marketing/marketing-action"
import { cn } from "@/lib/utils"
import { CALENDAR_PHONE_FIELD_COPY } from "@/lib/leads/calendar-phone-copy"
import { PHONE_COUNTRIES, countryFromTimeZone, isValidE164, toE164 } from "@/lib/phone-countries"
import { applicationVoiceChannelFor } from "@/lib/quo/phone-region"

// Mirrors the shape GET /api/apply/slots returns. Declared here rather than
// imported from the route file - lib/ and components/ never import from
// app/ (context/repo-organization.md audit #11).
type ApplySlotDay = { date: string; slots: string[] }

// The /apply booking picker (owner, 2026-07-31, replacing the Cal.com
// iframe/embed on the final application step): "we're not showing the full
// calendar anyways, so make it super simple for them... just have our own
// picker... only those three days." No card, no border, no white space above
// it - built directly into the step's content, same as every other question.
//
// Talks to our own two server routes (never Cal directly from the browser, so
// CAL_API_KEY never reaches the client): GET /api/apply/slots for the open
// times, POST /api/apply/book to create the real Cal.com booking. A booking
// made this way is indistinguishable from one made through Cal's own UI, so
// the webhook, the reminders cron, and reschedule/cancel links all keep
// working unchanged.

function formatDayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

function formatTimeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
}

/** The weekday of the slot he tapped, spelled out, for the show-up tick box. */
function formatWeekdayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long" })
}

/** Judge the SAME string the server will judge, through the same predicate.
 *
 * The two used to disagree - the button looked at national digits while the API
 * looked at the finished E.164 - so a long national number could enable the
 * button and then 400 on submit, which reads to the prospect as a broken site
 * on the last screen of the funnel. Converting first also means the country
 * selector is genuinely part of validity: the same ten digits are valid under
 * IL and invalid under US, which is exactly the distinction that was missing.
 */
function isValidPhone(country: string, value: string): boolean {
  if (!value.trim()) return false
  return isValidE164(toE164(country, value))
}

type LoadState = "loading" | "ready" | "empty" | "error"
type BookState = "idle" | "submitting" | "done" | "error"
type SlotsLoadFailureReason = "deadline" | "transport" | "http" | "invalid_response"

/** Outer ceiling for the availability flow. It covers both bounded server-side
 * Cal attempts plus response transit, while still preventing an endless
 * spinner on the one screen the whole funnel is gated on. A timeout drops the
 * picker into the existing error state, which offers Try again. */
const SLOTS_TIMEOUT_MS = 20_000

export function ApplyBookingPicker({
  name,
  email,
  program,
  prospectRef,
  awaitPendingSave,
  onSlotsLoadFailure,
  onBooked,
}: {
  name: string
  email: string
  program?: string | null
  /** Resolves once the form's queued answer saves have landed. The booking
   * route reads his latest show-up answer from the stored application, so a
   * booking posted before the save that carries his "Yes" would be refused. */
  awaitPendingSave?: () => Promise<unknown>
  /** The purpose-scoped booking_handoff reference the lead save handed back
   * (web/lib/prospects/signed-reference.ts). Posted as-is; /api/apply/book
   * verifies it before forwarding it into Cal metadata, and it is the only
   * thing that attributes this booking to the attempt. */
  prospectRef?: string | null
  /** One diagnostic for an active availability attempt that visibly fails. */
  onSlotsLoadFailure?: (failure: {
    reason: SlotsLoadFailureReason
    status?: number
    elapsedMs: number
  }) => void
  /** `submitProof` is the booked-submit proof /api/apply/book mints once Cal
   * confirms the booking (web/lib/leads/booked-submit.ts). Opaque here; the
   * form hands it to the submit so a booked applicant is never rate limited
   * out of filing the application he just booked for. */
  onBooked: (booking: { uid?: string; start?: string; end?: string; submitProof?: string }) => void
}) {
  const [loadState, setLoadState] = useState<LoadState>("loading")
  const [days, setDays] = useState<ApplySlotDay[]>([])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [phone, setPhone] = useState("")
  const [country, setCountry] = useState("US")
  const activeCountry =
    PHONE_COUNTRIES.find((c) => c.code === country) ?? PHONE_COUNTRIES[0]
  const enteredPhoneIsValid = phone.trim().length > 0 && isValidPhone(country, phone)
  const usesWhatsApp = enteredPhoneIsValid
    ? applicationVoiceChannelFor(toE164(country, phone)) === "whatsapp"
    : country !== "US" && country !== "CA"
  const [bookState, setBookState] = useState<BookState>("idle")
  const [bookError, setBookError] = useState<string | null>(null)
  // Show-up tick box: cleared whenever the time changes, because a tick is a
  // commitment to the slot it named and never carries over to another one.
  const [pledged, setPledged] = useState(false)
  const [confirmed, setConfirmed] = useState<string | null>(null)
  // Bumped by Try again, which re-runs the load effect.
  const [reloadKey, setReloadKey] = useState(0)
  const timeZoneRef = useRef<string>("America/New_York")
  // Keep the latest consumer without making parent renders restart the active
  // slots request. ApplicationFlow passes an inline callback because it needs
  // the current form attribution, so this seam intentionally stays outside
  // the request effect's dependencies.
  const onSlotsLoadFailureRef = useRef(onSlotsLoadFailure)

  useEffect(() => {
    onSlotsLoadFailureRef.current = onSlotsLoadFailure
  }, [onSlotsLoadFailure])

  // THE PHONE FIELD'S EARLY SAVE. A man who leaves a number and then never
  // books is the whole reason the field shows before a time is picked, so the
  // number is stored the moment it is a dialable one and he pauses typing, not
  // on Book. Silent by design: Book requires the number and sends it itself, so
  // a failed early save never blocks or warns him. `lastSavedPhoneRef` holds the E.164
  // last accepted so an unchanged number is never posted twice.
  const lastSavedPhoneRef = useRef("")
  useEffect(() => {
    if (!prospectRef || bookState === "submitting" || bookState === "done") return
    if (!isValidPhone(country, phone)) return
    const e164 = toE164(country, phone)
    if (e164 === lastSavedPhoneRef.current) return
    const timer = setTimeout(() => {
      void fetch("/api/apply/calendar-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phone, phoneCountry: country, prospectRef }),
        keepalive: true,
      })
        .then(async (res) => {
          const json = (await res.json().catch(() => null)) as { ok?: boolean } | null
          if (res.ok && json?.ok) lastSavedPhoneRef.current = e164
        })
        .catch(() => undefined)
    }, 1500)
    return () => clearTimeout(timer)
  }, [phone, country, email, prospectRef, bookState])

  useEffect(() => {
    let cancelled = false
    let failureReported = false
    let deadlineExpired = false
    const startedAt = performance.now()
    try {
      timeZoneRef.current = Intl.DateTimeFormat().resolvedOptions().timeZone || timeZoneRef.current
      // Open the selector on the country his own clock suggests, so the common
      // case needs no interaction. A guess, not a decision - the selector is
      // right there and he can change it, which is the point of adding it.
      setCountry(countryFromTimeZone(timeZoneRef.current))
    } catch {
      // Fall back to the default above.
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      deadlineExpired = true
      controller.abort()
    }, SLOTS_TIMEOUT_MS)
    const failLoad = (reason: SlotsLoadFailureReason, status?: number) => {
      if (cancelled || failureReported) return
      failureReported = true
      setLoadState("error")
      const elapsed = performance.now() - startedAt
      const elapsedMs = Number.isFinite(elapsed)
        ? Math.max(0, Math.min(SLOTS_TIMEOUT_MS, Math.round(elapsed)))
        : 0
      const boundedStatus =
        typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599
          ? status
          : undefined
      try {
        onSlotsLoadFailureRef.current?.({
          reason,
          ...(boundedStatus !== undefined ? { status: boundedStatus } : {}),
          elapsedMs,
        })
      } catch {
        // Analytics must never change the visible booking experience.
      }
    }
    void (async () => {
      try {
        const res = await fetch(`/api/apply/slots?tz=${encodeURIComponent(timeZoneRef.current)}`, {
          cache: "no-store",
          signal: controller.signal,
        })
        const json = (await res.json().catch(() => null)) as { ok?: boolean; days?: ApplySlotDay[] } | null
        if (cancelled) return
        if (deadlineExpired) {
          failLoad("deadline")
          return
        }
        if (!res.ok) {
          failLoad("http", res.status)
          return
        }
        if (!json?.ok || !Array.isArray(json.days)) {
          failLoad("invalid_response")
          return
        }
        if (json.days.length === 0) {
          // Genuinely no availability, which is a different thing from a failed
          // fetch and must not be reported as one: telling a man the calendar
          // broke when it is simply full sends him to a fallback page that
          // shows him the same empty calendar.
          setLoadState("empty")
          return
        }
        setDays(json.days)
        setSelectedDate(json.days[0].date)
        setLoadState("ready")
      } catch {
        if (!cancelled) failLoad(deadlineExpired ? "deadline" : "transport")
      } finally {
        clearTimeout(timer)
      }
    })()
    return () => {
      cancelled = true
      clearTimeout(timer)
      controller.abort()
    }
  }, [reloadKey])

  const selectedDay = days.find((d) => d.date === selectedDate) ?? null

  const phoneFormatError = phone.trim().length > 0 && !isValidPhone(country, phone)
  const phoneErrorId = bookError ? "apply-booking-phone-error" : undefined
  const phoneFormatErrorId = !bookError && phoneFormatError ? "apply-booking-phone-format" : undefined
  const phoneDescribedBy =
    [phoneErrorId, phoneFormatErrorId].filter((id): id is string => Boolean(id)).join(" ") || undefined

  async function confirmBooking() {
    if (!selectedSlot || !pledged || bookState === "submitting") return
    setBookState("submitting")
    setBookError(null)
    try {
      if (awaitPendingSave) await awaitPendingSave().catch(() => undefined)
      const res = await fetch("/api/apply/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start: selectedSlot,
          name,
          email,
          phone,
          phoneCountry: country,
          timeZone: timeZoneRef.current,
          program: program ?? undefined,
          prospectRef: prospectRef ?? undefined,
        }),
      })
      const json = (await res.json().catch(() => null)) as
        | { ok?: boolean; error?: string; uid?: string; start?: string; end?: string; submitProof?: string }
        | null
      if (!res.ok || !json?.ok) {
        setBookError(json?.error || "Couldn't book that time. Try another one.")
        setBookState("error")
        return
      }
      setBookState("done")
      setConfirmed(selectedSlot)
      onBooked({
        uid: json.uid,
        start: json.start ?? selectedSlot,
        end: json.end,
        ...(typeof json.submitProof === "string" ? { submitProof: json.submitProof } : {}),
      })
    } catch {
      setBookError("Couldn't book that time. Check your connection and try again.")
      setBookState("error")
    }
  }

  // The one phone field, required: labeled from the moment the calendar shows,
  // with nothing under it, and the booking's number once a time is picked.
  // Shown with the days in the ready state, and on its own when the calendar is
  // empty or failed to load, because a man who meets a calendar with nothing to
  // pick is exactly the one worth a call. It is the SAME field and the same
  // state either way, so what he typed carries over.
  const phoneBlock = (
    <div className="mt-4">
      <label
        htmlFor="apply-booking-phone"
        className="block text-[0.92rem] font-semibold text-[var(--color-ink)]"
      >
        {selectedSlot && usesWhatsApp ? "WhatsApp number" : CALENDAR_PHONE_FIELD_COPY.label}
      </label>
      {/* The country is CHOSEN, never assumed. Without this the field was a
          bare box with a US placeholder, and the API turned any ten digits
          into a +1 number: an Israeli prospect's 055-882-8248 became
          +10558828248, which nobody can dial, so every reminder went
          nowhere and he simply never turned up. */}
      <div className="mt-1.5 flex">
        <select
          aria-label="Country code"
          value={country}
          onChange={(event) => {
            setCountry(event.target.value)
            setBookError(null)
          }}
          className="min-h-11 shrink-0 rounded-none border border-r-0 border-[var(--color-rule)] bg-[var(--color-paper)] px-3 py-3 text-[0.95rem] text-[var(--color-ink)] outline-none transition-colors focus:border-[var(--color-vermilion)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)]"
        >
          {PHONE_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code === "OTHER" ? "Other" : `${c.code} +${c.dial}`}
            </option>
          ))}
        </select>
        <input
          id="apply-booking-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          aria-required="true"
          aria-invalid={phoneFormatError ? true : undefined}
          aria-describedby={phoneDescribedBy}
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value)
            setBookError(null)
          }}
          placeholder={activeCountry.example}
          className="w-full min-w-0 border border-[var(--color-rule)] bg-[var(--color-paper)] px-4 py-3 text-[1rem] text-[var(--color-ink)] outline-none transition-colors focus:border-[var(--color-vermilion)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]"
        />
      </div>
      {bookError ? (
        <p id={phoneErrorId} role="alert" className="mt-2 text-[0.88rem] font-medium text-[var(--color-vermilion)]">
          {bookError}
        </p>
      ) : null}
      {selectedSlot ? (
        <>
          {/* His approved wording, with the day and time of the slot he
              tapped filled in. A native checkbox in the form's one accent,
              the same treatment the age sliders use. */}
          <label className="mt-4 flex cursor-pointer items-start gap-3 text-[0.95rem] leading-[1.5] text-[var(--color-ink)]">
            <input
              type="checkbox"
              checked={pledged}
              onChange={(event) => {
                setPledged(event.target.checked)
                setBookError(null)
              }}
              style={{ accentColor: "var(--color-vermilion)" }}
              className="mt-[0.2rem] h-5 w-5 shrink-0 touch-manipulation"
            />
            <span>
              {`I'll be there on ${formatWeekdayLabel(selectedSlot)} at ${formatTimeLabel(selectedSlot)}. If something comes up, I'll reschedule instead of missing it.`}
            </span>
          </label>
          <button
            type="button"
            onClick={confirmBooking}
            disabled={bookState === "submitting" || !isValidPhone(country, phone) || !pledged}
            className={cn(marketingActionClass({ variant: "accent", size: "md" }), "mt-3 w-full")}
          >
            {bookState === "submitting" ? (
              <>
                <Loader2 size={16} className="animate-spin" aria-hidden />
                <span>Booking…</span>
              </>
            ) : (
              <>
                <span>
                  Confirm {formatDayLabel(selectedSlot)} at {formatTimeLabel(selectedSlot)}
                </span>
              </>
            )}
          </button>
        </>
      ) : null}
      {/* Only the genuinely useful case, matching how the form handles a
          malformed email: a man who has typed something and still sees a
          greyed button needs to know why. An empty field gets no filler
          line under it. */}
      {!bookError && phoneFormatError ? (
        <p
          id={phoneFormatErrorId}
          role="status"
          aria-live="polite"
          className="mt-2 text-[0.88rem] text-[var(--color-ink-soft)]"
        >
          That phone number doesn&apos;t look right.
        </p>
      ) : null}
    </div>
  )

  if (bookState === "done" && confirmed) {
    return (
      <p className="text-[0.98rem] leading-[1.5] text-[var(--color-ink)]">
        <span className="font-semibold">Booked: </span>
        <span>
          {formatDayLabel(confirmed)} at {formatTimeLabel(confirmed)}.
        </span>
      </p>
    )
  }

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 py-3 text-[0.92rem] text-[var(--color-ink-soft)]">
        <Loader2 size={16} className="animate-spin" aria-hidden />
        <span>Loading open times…</span>
      </div>
    )
  }

  if (loadState === "error" || loadState === "empty") {
    return (
      <div>
        <p className="text-[0.92rem] leading-[1.5] text-[var(--color-ink-soft)]">
          <span>
            {loadState === "empty"
              ? "No times are open right now. Try again in a little while, or use the link below."
              : "We couldn't load the open times."}
          </span>
        </p>
        <button
          type="button"
          onClick={() => {
            // Reset to loading HERE, in the event handler, not inside the load
            // effect: a synchronous setState in an effect body is a cascading
            // render the repo's react-hooks rule rejects.
            setLoadState("loading")
            setReloadKey((n) => n + 1)
          }}
          className="mt-2 text-[0.92rem] font-semibold text-[var(--color-vermilion)] underline underline-offset-4"
        >
          Try again
        </button>
        {phoneBlock}
      </div>
    )
  }

  return (
    <div>
      {/* Plain toggle buttons with aria-pressed, not role="tab": a tab without a
          tabpanel is invalid ARIA, and a radiogroup would owe arrow-key
          navigation the design does not have. */}
      <div role="group" aria-label="Choose a day" className="flex gap-2">
        {days.map((day) => {
          const selected = day.date === selectedDate
          return (
            <button
              key={day.date}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setSelectedDate(day.date)
                setSelectedSlot(null)
                setPledged(false)
                setBookError(null)
              }}
              className={cn(
                "min-h-11 flex-1 touch-manipulation rounded-[var(--radius-md)] border px-2 py-2.5 text-center text-[0.9rem] font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]",
                selected
                  ? "border-[var(--color-vermilion)] bg-[var(--color-paper-high)] text-[var(--color-ink)]"
                  : "border-[var(--color-rule)] bg-[var(--color-paper)] text-[var(--color-ink-muted)] hover:border-[var(--color-rule-strong)] hover:text-[var(--color-ink)]",
              )}
            >
              {formatDayLabel(day.slots[0])}
            </button>
          )
        })}
      </div>

      {/* A day with no slots is dropped server-side, so this line is a backstop
          rather than the normal path. It exists so a selected day can never
          render as a silent blank gap. */}
      {selectedDay && selectedDay.slots.length === 0 ? (
        <p className="mt-3 text-[0.92rem] text-[var(--color-ink-soft)]">
          No times left on this day. Pick another.
        </p>
      ) : null}

      {selectedDay && selectedDay.slots.length > 0 ? (
        <div className="mt-3 max-h-[12.5rem] overflow-y-auto">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {selectedDay.slots.map((iso) => {
              const selected = iso === selectedSlot
              return (
                <button
                  key={iso}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    if (iso !== selectedSlot) setPledged(false)
                    setSelectedSlot(iso)
                    setBookError(null)
                  }}
                  className={cn(
                    "min-h-11 touch-manipulation rounded-[var(--radius-sm)] border px-2 py-2 text-center text-[0.86rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)]",
                    selected
                      ? "border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-white"
                      : "border-[var(--color-rule)] bg-[var(--color-paper)] text-[var(--color-ink-muted)] hover:border-[var(--color-rule-strong)] hover:text-[var(--color-ink)]",
                  )}
                >
                  {formatTimeLabel(iso)}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {phoneBlock}
    </div>
  )
}
